import React, { useState, useEffect } from "react";
import { POLICIES_DATA } from "../data/policiesData";
import { COMPANY_CONFIG } from "../data/companyConfig";
import {
    X,
    ShieldCheck,
    FileText,
    RotateCcw,
    Lock,
    Printer,
    Phone,
    Mail,
    MapPin,
} from "lucide-react";

export const PoliciesModal = ({ initialType, isOpen, onClose }) => {
    const [activeType, setActiveType] = useState(initialType);

    // The modal stays mounted while closed, so without this the tab would keep
    // showing whichever policy was opened first.
    useEffect(() => {
        if (isOpen) setActiveType(initialType);
    }, [isOpen, initialType]);

    if (!isOpen) return null;

    const currentPolicy = POLICIES_DATA[activeType] || POLICIES_DATA.privacy;

    const tabs = [
        { id: "privacy", name: "Privacy Policy", icon: FileText },
        { id: "terms", name: "Terms & Conditions", icon: ShieldCheck },
        { id: "refund", name: "Refund Policy", icon: RotateCcw },
        { id: "grievance", name: "Grievance Officer", icon: ShieldCheck },
        { id: "regulatory", name: "Regulatory Info", icon: FileText },
        { id: "security", name: "Safety & Security", icon: Lock },
    ];

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
            <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col max-h-[90vh] overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/80">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-2.5 py-1 rounded-md">
                            Official Legal Document
                        </span>
                        <span className="text-xs text-slate-500 hidden sm:inline">
                            Global Bizness
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 transition-colors"
                            title="Print Document"
                        >
                            <Printer className="w-4 h-4" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-200/60 transition-colors"
                            id="modal-close-policy-btn"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-slate-200 bg-white px-6 overflow-x-auto">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeType === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveType(tab.id)}
                                className={`flex items-center gap-2 py-3 px-4 border-b-2 text-xs sm:text-sm font-bold whitespace-nowrap transition-colors cursor-pointer ${
                                    isActive
                                        ? "border-blue-600 text-blue-600"
                                        : "border-transparent text-slate-500 hover:text-slate-800"
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span>{tab.name}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Modal Body - Scrollable */}
                <div className="p-6 sm:p-8 overflow-y-auto space-y-6 text-slate-700 text-sm leading-relaxed">
                    {/* Document Title Banner */}
                    <div className="pb-4 border-b border-slate-200">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-['Outfit',sans-serif]">
                            {currentPolicy.title}
                        </h2>
                        <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                            <span>Effective Date: {currentPolicy.lastUpdated}</span>
                            <span>•</span>
                            <span className="text-emerald-700 font-medium">
                                Fully Compliant with Indian IT &amp; BBPS Norms
                            </span>
                        </div>
                        <p className="mt-3 text-xs sm:text-sm text-slate-600 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                            {currentPolicy.summary}
                        </p>
                    </div>

                    {/* Policy Sections */}
                    <div className="space-y-6">
                        {currentPolicy.sections.map((section, idx) => (
                            <div key={idx} className="space-y-2">
                                <h3 className="text-base font-bold text-slate-900 font-['Outfit',sans-serif]">
                                    {section.title}
                                </h3>
                                <div className="space-y-2 text-xs sm:text-sm text-slate-600">
                                    {section.content.map((paragraph, pIdx) => (
                                        <p key={pIdx} className="leading-relaxed">
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quick Contact & Escalation Box */}
                    <div className="mt-8 bg-blue-50/70 border border-blue-200 rounded-2xl p-5 space-y-3">
                        <h4 className="font-bold text-slate-900 text-xs sm:text-sm flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-blue-600" />
                            Corporate Grievance &amp; Compliance Office
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-700">
                            <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-blue-600 shrink-0" />
                                <span>Phone: {COMPANY_CONFIG.phone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-blue-600 shrink-0" />
                                <span>Email: {COMPANY_CONFIG.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-blue-600 shrink-0" />
                                <span className="truncate">
                                    Hitex Bizness Square, Hyderabad
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                        © 2026 {COMPANY_CONFIG.legalName}. All Rights Reserved.
                    </span>
                    <button
                        onClick={onClose}
                        className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 transition-colors"
                    >
                        I Understand &amp; Agree
                    </button>
                </div>
            </div>
        </div>
    );
};
